import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Sparkles, ArrowRight, Building2, Scale, Users, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BetaLandingPage() {
  const { language, setLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const tt = (en, es) => (language === 'es' ? es : en);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/waitlist`, { email, name: name || null });
      toast.success(r.data?.message || tt('Added to waitlist', 'Agregado a la lista de espera'));
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Failed', 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const trustPillars = [
    { to: '/why/verified-agents', icon: Users, en: 'Verified Agents', es: 'Agentes Verificados', sub_en: 'KYC-vetted real-estate professionals.', sub_es: 'Profesionales con KYC validado.' },
    { to: '/why/verified-properties', icon: ShieldCheck, en: 'Verified Properties', es: 'Propiedades Verificadas', sub_en: 'Documents reviewed by our team.', sub_es: 'Documentos revisados por nuestro equipo.' },
    { to: '/why/tracked-transactions', icon: Scale, en: 'Tracked Transactions', es: 'Transacciones Acompañadas', sub_en: 'Lawyer-supervised closings.', sub_es: 'Cierres supervisados por abogados.' },
  ];

  return (
    <div className="min-h-screen bg-[#0B192C] text-white relative overflow-hidden" data-testid="beta-landing-page">
      {/* Decorative grain / gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C] via-[#102236] to-[#0B192C] opacity-90" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#D4AF37] opacity-[0.06] blur-3xl -mr-32 -mt-32" />

      {/* Top bar */}
      <div className="relative max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[#D4AF37]" />
          <span className="font-heading text-xl font-bold">WakaCasa</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-[#D4AF37] text-[#0B192C] uppercase">Beta</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setLanguage(language === 'es' ? 'en' : 'es')} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1" data-testid="beta-lang-toggle">
            <Globe className="w-3.5 h-3.5" />{language === 'es' ? 'EN' : 'ES'}
          </button>
          <Link to="/auth?mode=register" data-testid="beta-login-link">
            <Button variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]">
              {tt('Have an invite? Sign in →', '¿Tienes invitación? Ingresar →')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="relative max-w-5xl mx-auto px-6 pt-12 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-medium mb-6" data-testid="beta-launching-pill">
          <Sparkles className="w-3.5 h-3.5" />{tt('Private beta — opening soon', 'Beta privada — apertura próximamente')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
          {tt('Verified Venezuelan', 'Inmuebles Venezolanos')}
          <br />
          <span className="text-[#D4AF37]">{tt('real estate.', 'verificados.')}</span>
        </h1>
        <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-10">
          {tt(
            'Every agent, every property, every closing — vetted by WakaCasa. We\'re polishing the experience with a small group of testers; join the waitlist and we\'ll let you in when we open to everyone.',
            'Cada agente, cada propiedad, cada cierre — validado por WakaCasa. Estamos puliendo la experiencia con un grupo limitado; únete a la lista y te avisamos cuando abramos al público.'
          )}
        </p>

        {/* Waitlist form */}
        {!done ? (
          <form onSubmit={submit} className="max-w-md mx-auto space-y-3" data-testid="beta-waitlist-form">
            <Input
              type="text"
              placeholder={tt('Your name (optional)', 'Tu nombre (opcional)')}
              value={name}
              onChange={e => setName(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-11"
              data-testid="beta-waitlist-name"
            />
            <div className="flex gap-2">
              <Input
                type="email"
                required
                placeholder={tt('Email address', 'Correo electrónico')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-11"
                data-testid="beta-waitlist-email"
              />
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-11 px-6 gap-1"
                data-testid="beta-waitlist-submit"
              >
                {tt('Notify me', 'Avísame')}<ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-500">
              {tt('We will email you once when WakaCasa opens publicly. No spam.', 'Te escribiremos solo cuando WakaCasa abra al público. Sin spam.')}
            </p>
          </form>
        ) : (
          <div className="max-w-md mx-auto bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-xl p-5 text-center" data-testid="beta-waitlist-done">
            <ShieldCheck className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="font-semibold text-white">{tt("You're on the list!", '¡Estás en la lista!')}</p>
            <p className="text-xs text-slate-300 mt-1">{tt("We'll email you when we open.", 'Te avisaremos cuando abramos.')}</p>
          </div>
        )}
      </div>

      {/* Solidarity message — Venezuela earthquake */}
      <div className="relative max-w-2xl mx-auto px-6 pb-4">
        <div className="border-l-2 border-[#D4AF37]/50 pl-4" data-testid="beta-solidarity">
          <p className="text-sm sm:text-[15px] text-slate-300 leading-relaxed">
            {tt(
              "From WakaCasa, we stand in solidarity with the families affected by the earthquake in Venezuela. In these difficult times, we send our deepest condolences and our support to all the people and communities impacted. We wish a swift recovery to the injured and strength to those facing this situation. We hope to help create and restore the homes and properties that have been affected.",
              'Desde WakaCasa nos solidarizamos con las familias afectadas por el terremoto en Venezuela. En estos momentos difíciles, enviamos nuestras más sinceras condolencias y nuestro apoyo a todas las personas y comunidades impactadas. Deseamos una pronta recuperación para los heridos y fortaleza para quienes enfrentan esta situación. Esperamos ayudar en crear y recuperar esos hogares e inmuebles que se han visto afectados.'
            )}
          </p>
        </div>
      </div>

      {/* Trust Pillars */}
      <div className="relative max-w-6xl mx-auto px-6 pb-16 pt-8">
        <p className="text-center text-xs uppercase tracking-widest text-slate-400 mb-6">
          {tt('Why WakaCasa', 'Por qué WakaCasa')}
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          {trustPillars.map(p => (
            <Link key={p.to} to={p.to} className="group block" data-testid={`beta-pillar-${p.to.split('/').pop()}`}>
              <div className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4AF37]/40 rounded-2xl p-5 transition-all h-full">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-3">
                  <p.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <p className="font-semibold text-white mb-1">{tt(p.en, p.es)}</p>
                <p className="text-xs text-slate-400 mb-3">{tt(p.sub_en, p.sub_es)}</p>
                <span className="text-[11px] text-[#D4AF37] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  {tt('Learn more', 'Más información')}<ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="relative max-w-7xl mx-auto px-6 py-6 border-t border-white/10 text-center text-[11px] text-slate-500">
        © {new Date().getFullYear()} WakaCasa.
        <Link to="/privacy" className="ml-3 hover:text-slate-300">{tt('Privacy', 'Privacidad')}</Link>
        <Link to="/terms" className="ml-3 hover:text-slate-300">{tt('Terms', 'Términos')}</Link>
        <PlatformDisclaimer className="mt-3 text-slate-500 max-w-3xl mx-auto" />
      </div>
    </div>
  );
}
